import { useSyncExternalStore } from "react"
import type Dexie from "dexie"
import type { Table } from "dexie"
import { useLiveQuery } from "dexie-react-hooks"
import { getDbEpoch, subscribeDbEpoch } from "../db-epoch"
import type { QueryFilter, ReactiveHookAdapter } from "../types"
import { compareByField } from "./compare"

/**
 * Track the global DB epoch as reactive React state. Included in each
 * `useLiveQuery` dependency list so a reopen (epoch bump) forces the live query
 * to re-subscribe on the fresh Dexie connection — otherwise the observer stays
 * bound to the closed one and reads empty forever (blank screen until reload).
 */
function useDbEpoch(): number {
  return useSyncExternalStore(subscribeDbEpoch, getDbEpoch, getDbEpoch)
}

function logReactiveReadError(
  table: string,
  filter: QueryFilter | undefined,
  err: unknown
): void {
  const name =
    err && typeof err === "object" && "name" in err
      ? (err as { name?: string }).name
      : undefined
  // eslint-disable-next-line no-console
  console.error(
    `[offline] reactive read failed for table "${table}" (${name ?? "Error"}) — returning empty result`,
    { table, filter, error: err }
  )
}

function isSchemaError(err: unknown): boolean {
  return (
    !!err &&
    typeof err === "object" &&
    "name" in err &&
    (err as { name?: string }).name === "SchemaError"
  )
}

/**
 * Ordered, full-table read (no `where` condition).
 *
 * Default — load the whole store + sort in memory (no index required), matching
 * the historical behavior so reads ordered by an UNindexed field (e.g.
 * client-groups by `-updatedAt`, prices by `createdAt`) keep working and aren't
 * truncated.
 *
 * When the caller passes an explicit `limit`, prefer a bounded Dexie cursor via
 * the ordering index (`orderBy(...).reverse().limit(...)`). This avoids the full
 * `getAll()` that Firefox rejects with `UnknownError` on a large store (the
 * emails table crashed the recap chat panel). If the field isn't indexed the
 * cursor throws `SchemaError` — synchronously OR async from `.toArray()` — so we
 * fall back to the in-memory sort in both cases.
 */
function orderedRead<T>(
  table: Table,
  orderBy: string,
  offset: number | undefined,
  limit: number | undefined
): Promise<T[]> {
  const desc = orderBy.startsWith("-")
  const field = desc ? orderBy.slice(1) : orderBy

  const jsSort = (): Promise<T[]> =>
    table.toArray().then((items: unknown[]) => {
      const rows = items as T[]
      rows.sort(compareByField<T>(field as keyof T, desc))
      let out = offset ? rows.slice(offset) : rows
      if (limit !== undefined) out = out.slice(0, limit)
      return out
    })

  // No explicit limit → original behavior (all rows, in-memory sort).
  if (limit === undefined) return jsSort()

  // Bounded read → try the index cursor, fall back to the JS sort on a
  // non-indexed field (sync throw OR async `SchemaError` from `.toArray()`).
  let cursorQuery: Promise<T[]>
  try {
    let coll = table.orderBy(field)
    if (desc) coll = coll.reverse()
    if (offset) coll = coll.offset(offset)
    cursorQuery = coll.limit(limit).toArray() as Promise<T[]>
  } catch {
    return jsSort()
  }
  return cursorQuery.catch((err) => {
    if (isSchemaError(err)) return jsSort()
    throw err
  })
}

/**
 * Single-condition read. Prefers the `where(key).equals(value)` index path;
 * when `key` isn't indexed Dexie throws `SchemaError` (sync OR async from
 * `.toArray()`), so we fall back to a full scan + in-memory equality filter.
 * Correct either way — the index path is just the fast one.
 */
function whereOrScan<T>(
  table: Table,
  key: string,
  value: unknown
): Promise<T[]> {
  const scan = (): Promise<T[]> =>
    table
      .toArray()
      .then((items: unknown[]) =>
        (items as T[]).filter(
          (it) => (it as Record<string, unknown>)[key] === value
        )
      )
  let indexQuery: Promise<T[]>
  try {
    indexQuery = table
      .where(key)
      .equals(value as string | number)
      .toArray() as Promise<T[]>
  } catch {
    return scan()
  }
  return indexQuery.catch((err) => {
    if (isSchemaError(err)) return scan()
    throw err
  })
}

export class DexieReactiveAdapter implements ReactiveHookAdapter {
  constructor(private db: Dexie) {}

  useQuery<T>(table: string, filter?: QueryFilter): T[] {
    const epoch = useDbEpoch()
    const result = useLiveQuery(
      () => {
        let query: Promise<T[]>
        try {
          const t = this.db.table(table)
          if (!filter) {
            query = t.toArray() as Promise<T[]>
          } else {
            const { limit, offset, orderBy, ...conditions } = filter
            const condEntries = Object.entries(conditions).filter(
              ([, v]) => v !== undefined
            )

            if (condEntries.length === 0 && orderBy) {
              // Ordered full-table read → bounded index cursor (Firefox-safe).
              query = orderedRead<T>(t, orderBy, offset, limit)
            } else if (condEntries.length === 1) {
              const [key, value] = condEntries[0]!
              query = whereOrScan<T>(t, key, value).then((items: T[]) =>
                postProcess(items, orderBy, offset, limit)
              )
            } else {
              const base = t.toArray() as Promise<T[]>
              query = base.then((items: T[]) => {
                const filtered = items.filter((item) =>
                  condEntries.every(
                    ([key, value]) =>
                      (item as Record<string, unknown>)[key] === value
                  )
                )
                return postProcess(filtered, orderBy, offset, limit)
              })
            }
          }
        } catch (err) {
          // Synchronous throw while building the query (bad index, etc.).
          logReactiveReadError(table, filter, err)
          return [] as T[]
        }
        // Asynchronous rejection from IndexedDB (Firefox UnknownError, quota, …).
        return query.catch((err) => {
          logReactiveReadError(table, filter, err)
          return [] as T[]
        })
      },
      // `epoch` re-subscribes the query on a DB reopen (see useDbEpoch).
      [table, JSON.stringify(filter), epoch],
      [] as T[]
    )
    return result ?? []
  }

  useOne<T>(table: string, id: string): T | null {
    const epoch = useDbEpoch()
    const result = useLiveQuery(
      () =>
        this.db
          .table(table)
          .get(id)
          .catch((err: unknown) => {
            logReactiveReadError(table, { id } as unknown as QueryFilter, err)
            return undefined
          }),
      // `epoch` re-subscribes the query on a DB reopen (see useDbEpoch).
      [table, id, epoch],
      null
    )
    return (result as T) ?? null
  }
}

/** In-memory orderBy + offset + limit for the post-`where` (already small) sets. */
function postProcess<T>(
  items: T[],
  orderBy: string | undefined,
  offset: number | undefined,
  limit: number | undefined
): T[] {
  let rows = items
  if (orderBy) {
    const desc = orderBy.startsWith("-")
    const field = desc ? orderBy.slice(1) : orderBy
    rows.sort(compareByField<T>(field as keyof T, desc))
  }
  if (offset) rows = rows.slice(offset)
  if (limit !== undefined) rows = rows.slice(0, limit)
  return rows
}
